'use client';

import { useCallback, useMemo, useRef, useEffect } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Address } from 'viem';
import {
  usePublicClient,
  useReadContract,
  useAccount,
  useChainId
} from 'wagmi';

import { poolContractAbi } from '@/abi';
import { calculateTokensRemaining } from '@/app/[locale]/(main)/(home)/_components/Pools/tokenDataUtils';
import USDCIcon from '@/assets/icons/usdc.svg';
import { Input } from '@/components/ui/input';
import { useBuyTokens } from '@/hooks/useBuyTokens';
import { useCursorPreservation } from '@/hooks/useCursorPreservation';
import { useSyncedTokenInputs } from '@/hooks/useSyncedTokenInputs';
import { useTokenImport } from '@/hooks/useTokenImport';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { cn } from '@/lib/utils/cn';
import { getDecimalsFromContract } from '@/lib/utils/getDecimalsFromContract';
import { removeCommas } from '@/lib/utils/removeCommas';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_POOL_TOKEN_DECIMALS } from '@/ts/constants/C_DECIMAL_PLACES';
import { C_ACTIVE_NETWORK_ID } from '@/ts/constants/C_NETWORKS';
import { C_USDC_DECIMALS } from '@/ts/constants/C_USDC_DECIMALS';
import { T_Pool } from '@/ts/types/T_Pool';

import { Label } from '../ui/label';

interface I_SwapAmountInputsProps {
  poolContractAddress: Address;
  tokenDecimals: number;
  usdcDecimals?: number;
  debounceMs?: number;
  className?: string;
  tokenPlaceholder?: string;
  usdcPlaceholder?: string;
  tokenSymbol: string;
  disabled?: boolean;
  tokenAddress?: Address;
  selectedToken?: T_Pool;
  onPoolDataUpdate?: (boughtTokenAmount: number) => void;
  closeModal?: () => void;
  shouldImportToken?: boolean;
  importTokenToWallet?: () => Promise<boolean>;
  onBuyTokensReady?: (
    handleBuy: () => Promise<void>,
    isBusy: boolean,
    usdcStr: string
  ) => void;
}

export function SwapAmountInputs({
  poolContractAddress,
  tokenDecimals,
  usdcDecimals = C_USDC_DECIMALS,
  debounceMs = 10,
  className = '',
  tokenPlaceholder = '0.000000',
  usdcPlaceholder = '0.000000',
  tokenSymbol,
  disabled = false,
  tokenAddress,
  selectedToken,
  onPoolDataUpdate,
  closeModal,
  shouldImportToken: shouldImportTokenProp,
  importTokenToWallet: importTokenToWalletProp,
  onBuyTokensReady
}: I_SwapAmountInputsProps) {
  const t = useTranslations('pools');
  const publicClient = usePublicClient();
  const { isConnected, address } = useAccount();
  const chainId = useChainId();
  const { buyTokens, state } = useBuyTokens(poolContractAddress);
  const { openWalletConnect, closeWalletConnect } = useWalletConnection();
  const globalUser = useGlobalData('user');
  const wasNotConnectedRef = useRef(false);
  const pendingUsdcAmountRef = useRef<string>('');
  const hasExecutedPurchaseRef = useRef(false);

  const currentPrice = selectedToken?.currentPrice || 0;
  const presalePrice =
    parseFloat(selectedToken?.presaleTokenPriceInUSDC || '0') || 0;
  const effectivePrice = currentPrice > 0 ? currentPrice : presalePrice;

  // Use provided import token props if available, otherwise use hook
  const tokenImportHook = useTokenImport({
    tokenAddress,
    tokenSymbol: selectedToken?.tokenSymbol || tokenSymbol,
    fallbackDecimals: tokenDecimals
  });

  const shouldImportToken =
    shouldImportTokenProp ?? tokenImportHook.shouldImportToken;
  const importTokenToWallet =
    importTokenToWalletProp ?? tokenImportHook.importTokenToWallet;
  const setShouldImportToken = tokenImportHook.setShouldImportToken;
  const isImporting = tokenImportHook.isImporting;

  const tokensRemaining = selectedToken
    ? calculateTokensRemaining(selectedToken)
    : '0';
  const tokensRemainingNum = parseFloat(tokensRemaining) || 0;
  const isTokensFinished = tokensRemainingNum <= 0;

  // Default to active network when wallet is not connected
  const activeChainId = isConnected ? chainId : C_ACTIVE_NETWORK_ID;

  // Read TOKEN_DECIMALS from contract
  const { data: contractTokenDecimals } = useReadContract({
    address: poolContractAddress,
    abi: poolContractAbi,
    functionName: 'TOKEN_DECIMALS',
    chainId: activeChainId,
    query: {
      enabled: !!poolContractAddress
    }
  }) as { data: bigint | undefined };

  // Use contract decimals if available, fallback to prop
  const actualTokenDecimals = useMemo(() => {
    if (contractTokenDecimals !== undefined) {
      return Number(getDecimalsFromContract(contractTokenDecimals));
    }
    return tokenDecimals;
  }, [contractTokenDecimals, tokenDecimals]);

  // Create stable async conversion functions using useCallback
  const getUsdcByTokens = useCallback(
    async (tokenAmount: bigint): Promise<bigint> => {
      if (!publicClient || !poolContractAddress) {
        throw new Error('Public client or pool contract address not available');
      }

      // Read tokenPrice and feePercent from contract
      const [tokenPrice, feePercent] = await Promise.all([
        publicClient.readContract({
          address: poolContractAddress,
          abi: poolContractAbi,
          functionName: 'getTokenPrice'
        }) as Promise<bigint>,
        publicClient.readContract({
          address: poolContractAddress,
          abi: poolContractAbi,
          functionName: 'feePercent'
        }) as Promise<bigint>
      ]);

      // Calculate USDC amount: (tokenAmount * tokenPrice * 10000) / (10000 - feePercent)
      // feePercent is in basis points (e.g., 100 = 1%, 1000 = 10%)
      const BASIS_POINTS = 10000n;
      const denominator = BASIS_POINTS - feePercent;

      if (denominator === 0n) {
        throw new Error('Fee percent cannot be 100%');
      }

      // Calculate: (tokenAmount * tokenPrice * 10000) / (10000 - feePercent)
      const numerator = tokenAmount * tokenPrice * BASIS_POINTS;
      const usdcAmountBeforeDecimals = numerator / denominator;

      // Divide by token decimals from contract (e.g., 10^18 for 18 decimals) to get correct format
      // This converts from the calculation result to USDC smallest units
      const tokenDecimalsMultiplier = BigInt(10 ** actualTokenDecimals);
      const usdcAmount = usdcAmountBeforeDecimals / tokenDecimalsMultiplier;

      return usdcAmount;
    },
    [publicClient, poolContractAddress, actualTokenDecimals]
  );

  const getTokensByUsdc = useCallback(
    async (usdcAmount: bigint): Promise<bigint> => {
      if (!publicClient || !poolContractAddress) {
        throw new Error('Public client or pool contract address not available');
      }

      const result = await publicClient.readContract({
        address: poolContractAddress,
        abi: poolContractAbi,
        functionName: 'getTokensByUsdc',
        args: [usdcAmount]
      });

      // getTokensByUsdc returns [tokensAmount, feeAmount] tuple
      // Extract the first element (tokensAmount)
      if (Array.isArray(result) && result.length > 0) {
        return result[0] as bigint;
      }
      // Fallback: if not an array, assume it's the value directly (shouldn't happen with this ABI)
      return result as bigint;
    },
    [publicClient, poolContractAddress]
  );

  const {
    tokenStr,
    usdcStr,
    onTokenChange,
    onUsdcChange,
    onTokenBlur,
    onUsdcBlur,
    isConverting,
    error
  } = useSyncedTokenInputs({
    tokenDecimals: actualTokenDecimals, // Contract decimals (for formatUnits)
    tokenDisplayDecimals: C_POOL_TOKEN_DECIMALS, // Pool token display decimals (for parsing/clamping/display)
    usdcDecimals,
    getUsdcByTokens,
    getTokensByUsdc,
    debounceMs
  });

  const usdcInputRef = useRef<HTMLInputElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);

  const { captureCursor: captureUsdcCursor } = useCursorPreservation(
    usdcInputRef,
    usdcStr
  );
  const { captureCursor: captureTokenCursor } = useCursorPreservation(
    tokenInputRef,
    tokenStr
  );

  const resetInputs = useCallback(() => {
    onTokenChange('');
    onUsdcChange('');
  }, [onTokenChange, onUsdcChange]);

  // Handle wallet connection and auto-execute purchase
  useEffect(() => {
    const isFullyAuthenticated = isConnected && !!address && !!globalUser;

    if (
      wasNotConnectedRef.current &&
      isFullyAuthenticated &&
      pendingUsdcAmountRef.current &&
      !hasExecutedPurchaseRef.current
    ) {
      hasExecutedPurchaseRef.current = true;

      const executePurchase = async () => {
        try {
          // Check if tokens are finished before proceeding
          if (!selectedToken) {
            toast.error(t('buyFailed'));
            return;
          }
          const currentTokensRemaining =
            calculateTokensRemaining(selectedToken);
          const currentTokensRemainingNum =
            parseFloat(currentTokensRemaining) || 0;
          if (currentTokensRemainingNum <= 0) {
            toast.error(t('tokensSoldOut'));
            wasNotConnectedRef.current = false;
            pendingUsdcAmountRef.current = '';
            hasExecutedPurchaseRef.current = false;
            closeWalletConnect();
            return;
          }

          if (shouldImportToken && tokenAddress) {
            const imported = await importTokenToWallet();
            if (!imported) {
              return;
            }
          }

          const result = await buyTokens(pendingUsdcAmountRef.current);

          if (result.success) {
            toast.success(t('tokensBoughtSuccessfully'));
            const usdcAmountWithoutCommas = removeCommas(
              pendingUsdcAmountRef.current
            );
            const usdcAmountNum = parseFloat(usdcAmountWithoutCommas) || 0;
            const price = effectivePrice || 0;
            const boughtTokenAmount =
              price > 0 && usdcAmountNum > 0 ? usdcAmountNum / price : 0;

            if (
              isFinite(boughtTokenAmount) &&
              !isNaN(boughtTokenAmount) &&
              boughtTokenAmount > 0
            ) {
              onPoolDataUpdate?.(boughtTokenAmount);
            }
            closeModal?.();
            resetInputs();
            setShouldImportToken(false);
          } else {
            toast.error(result.error || t('buyFailed'));
          }
        } catch (error) {
          toast.error(error instanceof Error ? error.message : t('buyFailed'));
        } finally {
          wasNotConnectedRef.current = false;
          pendingUsdcAmountRef.current = '';
          hasExecutedPurchaseRef.current = false;
        }
      };

      executePurchase();
    }
  }, [
    isConnected,
    address,
    globalUser,
    selectedToken,
    shouldImportToken,
    tokenAddress,
    importTokenToWallet,
    effectivePrice,
    buyTokens,
    onPoolDataUpdate,
    closeModal,
    closeWalletConnect,
    setShouldImportToken,
    resetInputs,
    t
  ]);

  const handleBuyTokens = useCallback(async () => {
    if (isTokensFinished) {
      toast.error(t('tokensSoldOut'));
      return;
    }

    const usdcAmountWithoutCommas = removeCommas(usdcStr);
    const usdcAmountNum = parseFloat(usdcAmountWithoutCommas) || 0;

    if (!usdcStr || usdcAmountNum <= 0) {
      toast.error(t('enterValidAmount'));
      return;
    }

    if (!isConnected || !globalUser) {
      wasNotConnectedRef.current = true;
      pendingUsdcAmountRef.current = usdcStr;
      openWalletConnect();
      return;
    }

    try {
      if (shouldImportToken && tokenAddress && importTokenToWallet) {
        const imported = await importTokenToWallet();
        if (!imported) {
          return;
        }
      } else {
        console.log('Skipping token import:', {
          shouldImportToken,
          tokenAddress,
          hasImportFunction: !!importTokenToWallet
        });
      }

      const result = await buyTokens(usdcStr);

      if (result.success) {
        toast.success(t('tokensBoughtSuccessfully'));
        const price = effectivePrice || 0;
        const boughtTokenAmount =
          price > 0 && usdcAmountNum > 0 ? usdcAmountNum / price : 0;

        if (
          isFinite(boughtTokenAmount) &&
          !isNaN(boughtTokenAmount) &&
          boughtTokenAmount > 0
        ) {
          onPoolDataUpdate?.(boughtTokenAmount);
        }
        closeModal?.();
        resetInputs();
        setShouldImportToken(false);
      } else {
        toast.error(result.error || t('buyFailed'));
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('buyFailed'));
    }
  }, [
    isTokensFinished,
    usdcStr,
    isConnected,
    globalUser,
    shouldImportToken,
    tokenAddress,
    importTokenToWallet,
    buyTokens,
    effectivePrice,
    onPoolDataUpdate,
    closeModal,
    resetInputs,
    setShouldImportToken,
    openWalletConnect,
    t
  ]);

  const isBusy =
    state.isLoading || state.isApproving || state.isBuying || isImporting;

  // Expose buy function, state, and USDC value to parent component
  useEffect(() => {
    onBuyTokensReady?.(handleBuyTokens, isBusy, usdcStr);
  }, [handleBuyTokens, isBusy, usdcStr, onBuyTokensReady]);

  return (
    <div className={cn('relative', className)}>
      <div className="flex">
        {/* USDC Input (Left Side) */}
        <div className="flex flex-col w-1/2">
          <Label htmlFor="usdc-input" className="mb-3">
            {t('enterAmount')}
          </Label>
          <div className="flex flex-1 bg-secondary w-full items-center rounded-tl-lg rounded-bl-lg px-3 py-1 relative">
            <USDCIcon fill="var(--background)" className="shrink-0" />
            <Input
              ref={usdcInputRef}
              id="usdc-input"
              className={cn(
                'bg-secondary text-base rounded-none ml-2 p-0 outline-none border-0 ring-0! text-background',
                error && 'text-destructive'
              )}
              value={usdcStr}
              onChange={e => {
                const adjustedValue = captureUsdcCursor(e);
                onUsdcChange(adjustedValue ?? e.target.value);
              }}
              onBlur={onUsdcBlur}
              placeholder={usdcPlaceholder}
              type="text"
              disabled={disabled || isBusy}
            />
          </div>
        </div>

        {/* Token Input (Right Side) */}
        <div className="flex flex-col w-1/2">
          <Label
            htmlFor="token-input"
            className="text-right inline-block mb-3 text-muted-foreground"
          >
            {t('youWillReceive')}
          </Label>
          <div className="flex w-full flex-1 bg-ghost-hover items-center rounded-lg rounded-tl-none rounded-bl-none px-3 py-1 relative">
            <Input
              ref={tokenInputRef}
              id="token-input"
              className={cn(
                'bg-ghost-hover text-base p-0 outline-none border-0 ring-0! text-foreground',
                error && 'text-destructive'
              )}
              value={tokenStr}
              onChange={e => {
                const adjustedValue = captureTokenCursor(e);
                onTokenChange(adjustedValue ?? e.target.value);
              }}
              onBlur={onTokenBlur}
              placeholder={tokenPlaceholder}
              type="text"
              disabled={disabled || isBusy}
            />
            <span className="text-base font-normal leading-normal ml-1 text-foreground">
              {tokenSymbol}
            </span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="absolute -bottom-6 left-0 text-sm text-destructive mt-1">
          {error}
        </div>
      )}
    </div>
  );
}
